import {
  fetchAnalyticsTimeSeries,
  buildSyntheticSeries,
  extractSelectedModels,
  parseSeriesDate,
  windowSeriesByRange,
  getActiveBrand,
  type TimeSeriesPoint,
  type SyntheticSeriesPoint,
} from "./analytics-overview-api";
import { fetchDeepSignalSeries, type SeriesIncompleteRun, type SeriesRebaselineMark } from "./deep-signal-series-api";
import { holdLastCaptured } from "./series-helpers";

export type TimeRange = "baseline" | "full";

export interface BestInCategoryDataPoint {
  date: string;
  timestamp: number;
  [modelKey: string]: number | string;
}

export interface BestInCategoryData {
  timeSeriesData: BestInCategoryDataPoint[];
  events: any[];
  /** Plotted runs flagged incomplete (Deep Signal source only). */
  incompleteRuns?: SeriesIncompleteRun[];
  /** Series rebaselines between plotted reads (Deep Signal source only). */
  rebaselineMarks?: SeriesRebaselineMark[];
  selectedModels: string[];
  isSynthetic?: boolean;
}

/**
 * Map a raw time-series point to a chart data point.
 *
 * FIX: the previous version fell back to `overall || 0`, meaning a genuine
 * overall value of 0 would silently propagate as 0 for every model key,
 * collapsing all chart lines. Now we use `overall` (even if 0) explicitly
 * as the fallback for missing per-model data.
 */
function toDataPoint(
  p: TimeSeriesPoint | SyntheticSeriesPoint,
  idx: number,
  models: string[],
): BestInCategoryDataPoint {
  const overall = (p as any).overall ?? 0;          // real 0 is valid data
  // Use the real calendar timestamp so the chart's time axis can place each
  // point (and any events) at its true date. Fall back to the sequential index
  // only if the date is unparseable, so a bad date never collapses the axis.
  const parsed = parseSeriesDate(p.date);
  const result: BestInCategoryDataPoint = {
    date: p.date,
    timestamp: Number.isNaN(parsed) ? idx : parsed,
  };
  for (const key of models) {
    const modelVal = (p as any)[key];
    // Use per-model value when present; fall back to overall (not 0)
    result[key] = modelVal !== undefined && modelVal !== null ? modelVal : overall;
  }
  result.overall = overall;       // composite, for the headline (not a plotted line)
  return result;
}

export async function fetchBestInCategoryData(
  timeRange: TimeRange,
  antennaData?: any,
  selectedModels?: string[],
): Promise<BestInCategoryData> {
  // Primary source: Deep Signal runs. Top-5 Rate is the Deep Signal funnel's
  // top5_rate, measured per run, per model.
  try {
    const ds = await fetchDeepSignalSeries(getActiveBrand());
    if (ds.runCount > 0) {
      const { series, models } = holdLastCaptured(ds.top5, ds.selectedModels);
      return {
        timeSeriesData: windowSeriesByRange(series, timeRange),
        events:         ds.publicationEvents,
        incompleteRuns: ds.incompleteRuns,
        rebaselineMarks: ds.rebaselineMarks,
        selectedModels: models,
        isSynthetic:    false,
      };
    }
  } catch { /* fall through to monitoring */ }

  try {
    // Fallback: Brand Monitoring snapshots. Fetch the full 90d window and slice
    // client-side (below) so every chart + the overview share ONE de-dup key
    // (brand|90), keeping the write-on-read endpoint to a single hit per load
    // regardless of time-range clicks.
    const ts = await fetchAnalyticsTimeSeries(undefined, 90);
    const windowed = windowSeriesByRange(ts.best_in_category_series, timeRange);

    if (windowed.length > 0) {
      return {
        timeSeriesData: windowed.map((p, i) => toDataPoint(p, i, ts.selected_models)),
        events:         [],
        selectedModels: ts.selected_models,
        isSynthetic:    false,
      };
    }
  } catch {
    // No snapshots yet, fall through to synthetic
  }

  // Synthetic fallback: derive a single data point from the last antenna run
  const models    = extractSelectedModels(antennaData, selectedModels);
  const synthetic = buildSyntheticSeries(antennaData, "bestInCategory", models);

  return {
    timeSeriesData: synthetic.map((p, i) => toDataPoint(p, i, models)),
    events:         [],
    selectedModels: models,
    isSynthetic:    true,
  };
}